import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navbar,
  Nav,
  Container,
  Badge,
  Button,
  Offcanvas,
  Dropdown
} from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { logout } from '../../store/slices/authSlice';
import { RootState, AppDispatch } from '../../store';
import { getGetAuthMeQueryKey } from '../../api/generated/arrayscapeGamingDashboardAPI';
import NotificationDropdown from './NotificationDropdown';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { useTheme } from '../../contexts/ThemeContext';
import { getRoleBadgeVariant, getRoleDisplayName } from '../../utils/roles';
import { APP_VERSION } from '../../version';

const Header: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { user, isImpersonating } = useSelector((state: RootState) => state.auth);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const { mode, effectiveTheme, cycleMode } = useTheme();

  const getThemeIcon = () => {
    if (mode === 'system') return 'bi-circle-half';
    if (effectiveTheme === 'dark') return 'bi-moon-stars-fill';
    return 'bi-sun-fill';
  };

  const getThemeLabel = () => {
    if (mode === 'system') return 'System theme';
    if (mode === 'dark') return 'Dark mode';
    return 'Light mode';
  };

  // Fetch company name from public settings using React Query (with deduplication)
  const { data: settings } = usePublicSettings();
  const companyName = settings?.companyName || 'Company';

  // Update document title when company name changes
  useEffect(() => {
    document.title = `${companyName} Dashboard`;
  }, [companyName]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleStopImpersonating = () => {
    document.cookie = 'impersonate=; path=/; max-age=0';
    window.location.reload();
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    return `${user.first_name} ${user.last_name}`;
  };


  return (
    <>
      <Navbar expand="lg" className="border-bottom sticky-top" style={{ zIndex: 1030 }}>
        <Container fluid>
          <Navbar.Brand href="/dashboard" className="fw-bold text-primary fs-4">
            <i className="bi bi-graph-up-arrow me-2"></i>
            {companyName} Dashboard
          </Navbar.Brand>

          <div className="d-flex align-items-center">
            <NotificationDropdown />

            <Button
              variant="link"
              className="text-decoration-none text-body-secondary ms-2 p-1"
              onClick={cycleMode}
              aria-label={getThemeLabel()}
              title={getThemeLabel()}
            >
              <i className={`bi ${getThemeIcon()} fs-5`}></i>
            </Button>

            <Button
              variant="outline-secondary"
              className="d-lg-none ms-2"
              onClick={() => setShowOffcanvas(true)}
              aria-label="Toggle navigation"
            >
              <i className="bi bi-list"></i>
            </Button>

            <div className="d-none d-lg-block ms-3">
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link"
                  className="btn-link text-decoration-none text-body border-0 shadow-none p-0 d-flex align-items-center"
                  style={{ background: 'none' }}
                >
                  <i className={`bi ${isImpersonating ? 'bi-person-badge' : 'bi-person-circle'} me-2 fs-5`}></i>
                  <span className="me-1">{getUserDisplayName()}</span>
                  {isImpersonating && (
                    <Badge bg="warning" text="dark" className="ms-1 small">Impersonating</Badge>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-sm" style={{ minWidth: '250px', ...(isImpersonating ? { backgroundColor: 'var(--app-highlight-bg)' } : {}) }}>
                  <div className="px-3 py-2 border-bottom">
                    <div className="d-flex align-items-center mb-2">
                      <i className={`bi ${isImpersonating ? 'bi-person-badge' : 'bi-person-circle'} me-2 fs-4 text-muted`}></i>
                      <div>
                        <div className="fw-semibold">{getUserDisplayName()}</div>
                        {user?.email && (
                          <div className="small text-muted">{user.email}</div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      {isImpersonating && (
                        <Badge bg="warning" text="dark" className="small">
                          <i className="bi bi-person-badge me-1"></i>
                          Impersonating
                        </Badge>
                      )}
                      {user?.role && (
                        <Badge
                          bg={getRoleBadgeVariant(user.role)}
                          className="small"
                        >
                          {getRoleDisplayName(user.role)}
                        </Badge>
                      )}
                      {user?.territory && (
                        <Badge
                          bg="secondary"
                          className="small"
                        >
                          {user.territory}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {isImpersonating && (
                    <>
                      <Dropdown.Item onClick={handleStopImpersonating} className="py-2">
                        <i className="bi bi-x-lg me-2"></i>
                        Stop Impersonating
                      </Dropdown.Item>
                      <Dropdown.Divider />
                    </>
                  )}
                  <Dropdown.Item href="/profile" className="py-2">
                    <i className="bi bi-person me-2"></i>
                    Profile
                  </Dropdown.Item>
                  <Dropdown.Item href="/settings" className="py-2">
                    <i className="bi bi-gear me-2"></i>
                    Settings
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout} className="py-2">
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Sign Out
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <div className="px-3 py-2">
                    <small className="text-muted">Version {APP_VERSION}</small>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Container>
      </Navbar>

      <Offcanvas 
        show={showOffcanvas} 
        onHide={() => setShowOffcanvas(false)} 
        placement="end"
        className="d-lg-none"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-flex flex-column">
            <div className="mb-3 p-3 rounded" style={{ backgroundColor: isImpersonating ? '#fff3cd' : undefined }}>
              <div className="d-flex align-items-center mb-2">
                <i className={`bi ${isImpersonating ? 'bi-person-badge' : 'bi-person-circle'} me-2 fs-4`}></i>
                <div className="flex-grow-1">
                  <div className="fw-semibold">{getUserDisplayName()}</div>
                  {user?.email && (
                    <div className="small text-muted">{user.email}</div>
                  )}
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {isImpersonating && (
                  <Badge bg="warning" text="dark" className="small">
                    <i className="bi bi-person-badge me-1"></i>
                    Impersonating
                  </Badge>
                )}
                {user?.role && (
                  <Badge
                    bg={getRoleBadgeVariant(user.role)}
                    className="small"
                  >
                    {getRoleDisplayName(user.role)}
                  </Badge>
                )}
                {user?.territory && (
                  <Badge
                    bg="secondary"
                    className="small"
                  >
                    {user.territory}
                  </Badge>
                )}
              </div>
            </div>

            <Nav className="flex-column">
              {isImpersonating && (
                <>
                  <Nav.Link onClick={handleStopImpersonating} className="py-2">
                    <i className="bi bi-x-lg me-2"></i>
                    Stop Impersonating
                  </Nav.Link>
                  <hr />
                </>
              )}
              <Nav.Link href="/profile" className="py-2">
                <i className="bi bi-person me-2"></i>
                Profile
              </Nav.Link>
              <Nav.Link href="/settings" className="py-2">
                <i className="bi bi-gear me-2"></i>
                Settings
              </Nav.Link>
              <hr />
              <Nav.Link onClick={handleLogout} className="py-2 text-danger">
                <i className="bi bi-box-arrow-right me-2"></i>
                Sign Out
              </Nav.Link>
            </Nav>

            <div className="mt-auto pt-3 border-top">
              <small className="text-muted px-3">Version {APP_VERSION}</small>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default Header;
